import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Avatar, EligibilityBadge, Loading, inputCls, labelCls, btnPrimary } from "@/components/kit";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { GraduationCap, Plus, CheckCircle, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

const courses = ["Algebra II", "Biology", "English Lit", "World History", "PE"];
const periods = ["Period 1", "Period 2", "Period 3", "Period 4", "Period 5", "Period 6"];
const statuses = [
  { v: "present", c: "#34C759" }, { v: "absent", c: "#FF3B30" },
  { v: "tardy", c: "#FF9F0A" }, { v: "excused", c: "#0A84FF" },
];

const thisMonday = () => {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

export default function Academics() {
  const { user } = useAuth();
  const [students, setStudents] = useState(null);
  const [selected, setSelected] = useState(null);
  const [grades, setGrades] = useState(null);
  const [att, setAtt] = useState(null);
  const canEnter = ["teacher", "athletic_director"].includes(user?.role);
  const [gForm, setGForm] = useState({ subject: "Algebra II", score: "", term: "Term 3", week_of: thisMonday() });
  const [aForm, setAForm] = useState({ date: new Date().toISOString().slice(0, 10), status: "present", subject: "Algebra II", period: "Period 1" });

  useEffect(() => {
    if (user?.role === "athlete") {
      setStudents([{ id: user.id, name: user.name, sport: user.sport }]);
      setSelected(user.id);
    } else {
      api.get("/users", { params: { role: "athlete" } }).then((r) => {
        setStudents(r.data);
        if (r.data[0]) setSelected(r.data[0].id);
      });
    }
  }, [user]);

  const loadStudent = async (sid) => {
    setGrades(null); setAtt(null);
    const [gt, at] = await Promise.all([api.get("/grades/trends", { params: { student_id: sid } }), api.get("/attendance/trends", { params: { student_id: sid } })]);
    setGrades(gt.data); setAtt(at.data);
  };
  useEffect(() => { if (selected) loadStudent(selected); }, [selected]);

  const addGrade = async () => {
    if (!gForm.score) { toast.error("Enter a score"); return; }
    try { await api.post("/grades", { student_id: selected, subject: gForm.subject, score: parseFloat(gForm.score), term: gForm.term, week_of: gForm.week_of }); toast.success("Weekly grade saved"); setGForm({ ...gForm, score: "" }); loadStudent(selected); }
    catch (e) { toast.error("Could not save"); }
  };
  const markAtt = async () => {
    try { await api.post("/attendance", { student_id: selected, date: aForm.date, status: aForm.status, subject: aForm.subject, period: aForm.period }); toast.success("Attendance marked"); loadStudent(selected); }
    catch (e) { toast.error("Could not save"); }
  };

  if (!students) return <Loading />;
  const sel = students.find((s) => s.id === selected);

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-7xl mx-auto">
      <PageHeader eyebrow="Grades & Attendance" title="Academics" icon={GraduationCap} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 items-start">
        {/* Student list */}
        {user?.role !== "athlete" && (
          <Card className="p-3 lg:col-span-1">
            <div className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold px-2 py-2">Students</div>
            <div className="space-y-1 max-h-[70vh] overflow-y-auto">
              {students.map((s) => (
                <button key={s.id} onClick={() => setSelected(s.id)} data-testid={`student-${s.id}`}
                  className={`w-full flex items-center gap-2 px-2 py-2 rounded-sm text-left transition-colors ${selected === s.id ? "bg-[#FF3B30]/15" : "hover:bg-white/5"}`}>
                  <Avatar name={s.name} size={32} color="#34C759" />
                  <div className="min-w-0 flex-1"><div className="text-sm text-white truncate">{s.name}</div><div className="text-[11px] text-[#737373]">{s.sport}</div></div>
                  {s.eligible === false && <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] shrink-0" title="Ineligible" data-testid={`roster-ineligible-dot-${s.id}`} />}
                </button>
              ))}
            </div>
          </Card>
        )}

        <div className={user?.role === "athlete" ? "lg:col-span-4" : "lg:col-span-3"}>
          {!grades || !att ? <Loading /> : (
            <div className="space-y-6">
              {sel && <div className="flex items-center gap-3"><Avatar name={sel.name} size={44} color="#34C759" /><div><div className="font-head text-2xl font-bold uppercase tracking-tight">{sel.name}</div><div className="text-xs text-[#737373]">{sel.sport}</div></div>{sel.role === "athlete" && <div className="ml-auto"><EligibilityBadge eligible={sel.eligible} testid={`acad-elig-${sel.id}`} /></div>}</div>}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">GPA Avg</div><div className="font-mono text-3xl font-black" data-testid="acad-gpa">{grades.overall_average}</div></Card>
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Attendance</div><div className="font-mono text-3xl font-black text-[#34C759]" data-testid="acad-attendance">{att.present_rate}%</div></Card>
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Absences</div><div className="font-mono text-3xl font-black text-[#FF3B30]">{att.counts.absent}</div></Card>
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Tardies</div><div className="font-mono text-3xl font-black text-[#FF9F0A]">{att.counts.tardy}</div></Card>
              </div>

              {/* Grade trend chart */}
              <Card className="p-6" data-testid="grade-chart">
                <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4">Grade Trend</div>
                {grades.timeline.length ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={grades.timeline} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="date" stroke="#737373" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} tickFormatter={(d) => d.slice(5)} />
                      <YAxis domain={[40, 100]} stroke="#737373" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2, fontFamily: "JetBrains Mono" }} />
                      <Line type="monotone" dataKey="score" stroke="#FF3B30" strokeWidth={2} dot={{ r: 3, fill: "#FF3B30" }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div className="text-sm text-[#737373] py-8 text-center">No grades yet.</div>}
              </Card>

              {/* Subject averages */}
              <Card className="p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4">By Course (weekly avg)</div>
                <div className="space-y-3">
                  {grades.subjects.map((s) => (
                    <div key={s.subject}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white">{s.subject}</span>
                        <span className="font-mono text-[#A3A3A3]">{s.average} · {s.weekly?.length || 0} wks</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-sm overflow-hidden"><div className="h-full bg-[#FF3B30]" style={{ width: `${s.average}%` }} /></div>
                    </div>
                  ))}
                  {grades.subjects.length === 0 && <div className="text-sm text-[#737373]">No courses recorded.</div>}
                </div>
              </Card>

              {/* Attendance breakdown */}
              <Card className="p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4">Attendance ({att.total} records)</div>
                <div className="grid grid-cols-4 gap-3 mb-5">
                  {statuses.map((s) => (
                    <div key={s.v} className="text-center bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                      <div className="font-mono text-2xl font-black" style={{ color: s.c }}>{att.counts[s.v]}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{s.v}</div>
                    </div>
                  ))}
                </div>
                {att.by_class?.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold mb-2">By Class</div>
                    <div className="space-y-2" data-testid="attendance-by-class">
                      {att.by_class.map((c) => (
                        <div key={c.class} className="flex items-center gap-3 text-sm">
                          <span className="text-white w-32 truncate">{c.class}</span>
                          <div className="flex-1 h-2 bg-white/5 rounded-sm overflow-hidden">
                            <div className="h-full" style={{ width: `${c.rate}%`, background: c.rate >= 92 ? "#34C759" : "#FF9F0A" }} />
                          </div>
                          <span className="font-mono text-[#A3A3A3] w-12 text-right">{c.rate}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card>

              {/* Teacher entry */}
              {canEnter && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4"><ClipboardText size={16} weight="fill" color="#FF3B30" /> Add Weekly Grade</div>
                    <div className="space-y-3">
                      <div>
                        <label className={labelCls}>Course</label>
                        <select className={inputCls} value={gForm.subject} onChange={(e) => setGForm({ ...gForm, subject: e.target.value })} data-testid="grade-subject-select">{courses.map((s) => <option key={s}>{s}</option>)}</select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Week of</label>
                          <input className={inputCls} type="date" value={gForm.week_of} onChange={(e) => setGForm({ ...gForm, week_of: e.target.value })} data-testid="grade-week-input" />
                        </div>
                        <div>
                          <label className={labelCls}>Score</label>
                          <input className={inputCls} type="number" placeholder="0-100" value={gForm.score} onChange={(e) => setGForm({ ...gForm, score: e.target.value })} data-testid="grade-score-input" />
                        </div>
                      </div>
                      <button onClick={addGrade} className={`${btnPrimary} w-full`} data-testid="save-grade-button"><Plus size={16} weight="bold" /> Save Weekly Grade</button>
                    </div>
                  </Card>
                  <Card className="p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4"><CheckCircle size={16} weight="fill" color="#FF3B30" /> Mark Class Attendance</div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Class</label>
                          <select className={inputCls} value={aForm.subject} onChange={(e) => setAForm({ ...aForm, subject: e.target.value })} data-testid="att-class-select">{courses.map((s) => <option key={s}>{s}</option>)}</select>
                        </div>
                        <div>
                          <label className={labelCls}>Period</label>
                          <select className={inputCls} value={aForm.period} onChange={(e) => setAForm({ ...aForm, period: e.target.value })} data-testid="att-period-select">{periods.map((p) => <option key={p}>{p}</option>)}</select>
                        </div>
                      </div>
                      <input className={inputCls} type="date" value={aForm.date} onChange={(e) => setAForm({ ...aForm, date: e.target.value })} data-testid="att-date-input" />
                      <div className="grid grid-cols-4 gap-2">
                        {statuses.map((s) => (
                          <button key={s.v} onClick={() => setAForm({ ...aForm, status: s.v })} data-testid={`att-status-${s.v}`}
                            className={`py-2 rounded-sm text-[10px] uppercase tracking-wider font-semibold transition-colors ${aForm.status === s.v ? "text-white" : "bg-[#0A0A0A] border border-white/15 text-[#A3A3A3]"}`}
                            style={aForm.status === s.v ? { background: s.c } : {}}>{s.v}</button>
                        ))}
                      </div>
                      <button onClick={markAtt} className={`${btnPrimary} w-full`} data-testid="save-attendance-button"><CheckCircle size={16} weight="bold" /> Mark</button>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
